import type { CSSProperties } from 'react'

import type { I18TranslationKey } from '../../../types'
import type { TableActionType, TableInsertDirection, TableSelectionAxis } from './core'
import { CONTROLS_GAP, TRIGGER_THICKNESS } from './TableControls.styles'

export type InsertIndicatorState = {
    axis: TableSelectionAxis
    direction: TableInsertDirection
    index: number
    top: number
    left: number
    width: number
    height: number
} | null

export type TableToolbarDropdown = 'textColor' | 'bgColor' | 'align'

export type FloatingSurfaceState =
    | {
          kind: 'contextMenu'
          x: number
          y: number
      }
    | {
          kind: 'dropdown'
          dropdown: TableToolbarDropdown
      }
    | null

export type TableStructureActionType = Extract<TableActionType, 'deleteRow' | 'deleteColumn' | 'mergeCells' | 'splitCell'>
export type TableContextMenuActionType = TableActionType

export type TableAxisRenderConfig = {
    axis: TableSelectionAxis
    triggerStyle: (rect: DOMRect) => CSSProperties
    hotspotTitle: I18TranslationKey
}

export const ALIGN_OPTIONS = [
    { label: 'alignLeft', icon: 'TextAlignLeft', value: 'left' as const },
    { label: 'alignCenter', icon: 'TextAlignCenter', value: 'center' as const },
    { label: 'alignRight', icon: 'TextAlignRight', value: 'right' as const }
]

export const STYLE_ACTIONS = [
    { type: 'bold' as const, icon: 'TextBolder', label: 'bold' },
    { type: 'italic' as const, icon: 'TextItalic', label: 'italic' },
    { type: 'underline' as const, icon: 'TextUnderline', label: 'underline' },
    { type: 'strike' as const, icon: 'TextStrikethrough', label: 'strikethrough' }
]

export const DEFAULT_ACTIVE_STYLE_MAP = {
    bold: false,
    italic: false,
    underline: false,
    strike: false
}

export const AXIS_RENDER_CONFIGS: TableAxisRenderConfig[] = [
    {
        axis: 'column',
        triggerStyle: rect => ({
            top: rect.top - CONTROLS_GAP - TRIGGER_THICKNESS,
            left: rect.left,
            width: rect.width,
            height: TRIGGER_THICKNESS
        }),
        hotspotTitle: 'insertColumn'
    },
    {
        axis: 'row',
        triggerStyle: rect => ({
            top: rect.top,
            left: rect.left - CONTROLS_GAP - TRIGGER_THICKNESS,
            width: TRIGGER_THICKNESS,
            height: rect.height
        }),
        hotspotTitle: 'insertRow'
    }
]

export const STRUCTURE_ACTION_CONFIG: Record<TableStructureActionType, { icon: string; label: I18TranslationKey }> = {
    deleteRow: { icon: 'Trash', label: 'deleteRow' },
    deleteColumn: { icon: 'Trash', label: 'deleteColumn' },
    mergeCells: { icon: 'NewTable', label: 'mergeCells' },
    splitCell: { icon: 'Columns', label: 'splitCell' }
}

export const CONTEXT_MENU_ACTION_CONFIG: Record<
    TableContextMenuActionType,
    { label: I18TranslationKey; danger?: boolean; section: 'insert' | 'structure' | 'header' | 'table' }
> = {
    insertRowBefore: { label: 'insertRowAbove', section: 'insert' },
    insertRowAfter: { label: 'insertRowBelow', section: 'insert' },
    insertColumnBefore: { label: 'insertColumnLeft', section: 'insert' },
    insertColumnAfter: { label: 'insertColumnRight', section: 'insert' },
    deleteRow: { label: 'deleteRow', danger: true, section: 'structure' },
    deleteColumn: { label: 'deleteColumn', danger: true, section: 'structure' },
    mergeCells: { label: 'mergeCells', section: 'structure' },
    splitCell: { label: 'splitCell', section: 'structure' },
    toggleHeaderRow: { label: 'toggleHeaderRow', section: 'header' },
    toggleHeaderColumn: { label: 'toggleHeaderColumn', section: 'header' },
    deleteTable: { label: 'deleteTable', danger: true, section: 'table' }
}
